import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { 
  FlaskConical, 
  Search, 
  Star, 
  MapPin, 
  Home, 
  Building2, 
  Clock, 
  CheckCircle, 
  ShoppingCart,
  Plus,
  Minus,
  Calendar,
  CreditCard,
  Banknote,
  Loader2,
  Phone,
  Mail,
  User,
  Package,
  FileText,
  X,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const DiagnosticsBooking = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [tests, setTests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showCartDialog, setShowCartDialog] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [timeSlots, setTimeSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    bookingType: 'home_collection',
    bookingDate: '',
    bookingTime: '',
    patientName: user?.full_name || '',
    patientPhone: '',
    patientEmail: user?.email || '',
    address: '',
    city: '',
    pincode: '',
    paymentMethod: 'pay_at_center'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [providersRes, testsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/diagnostics/providers`),
        axios.get(`${API}/diagnostics/tests`),
        axios.get(`${API}/diagnostics/bookings`)
      ]);
      setProviders(providersRes.data.providers);
      setTests(testsRes.data.tests);
      setBookings(bookingsRes.data.bookings);
    } catch (error) {
      toast.error('Failed to load diagnostics data');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeSlots = async (date) => {
    if (!date || !selectedProvider) return;
    try {
      const response = await axios.get(`${API}/diagnostics/time-slots`, {
        params: {
          date,
          provider_id: selectedProvider.id,
          booking_type: bookingForm.bookingType
        }
      });
      setTimeSlots(response.data.slots);
    } catch (error) {
      toast.error('Failed to load time slots');
    }
  };

  const addToCart = (test) => {
    if (cart.find(t => t.id === test.id)) {
      toast.info('Test already in cart');
      return;
    }
    setCart([...cart, test]);
    toast.success(`${test.name} added to cart`);
  };

  const removeFromCart = (testId) => {
    setCart(cart.filter(t => t.id !== testId));
  };

  const getCartTotal = () => {
    let total = cart.reduce((sum, test) => sum + (test.discounted_price || test.price), 0);
    if (bookingForm.bookingType === 'home_collection') {
      total += 100; // Home collection charge
    }
    return total;
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || test.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleStartBooking = (provider) => {
    if (cart.length === 0) {
      toast.error('Please add tests to cart first');
      return;
    }
    setSelectedProvider(provider);
    setBookingStep(1);
    setShowBookingDialog(true);
  };

  const handleDateChange = (date) => {
    setBookingForm({ ...bookingForm, bookingDate: date, bookingTime: '' });
    loadTimeSlots(date);
  };

  const handleSubmitBooking = async () => {
    // Validate form
    if (!bookingForm.bookingDate || !bookingForm.bookingTime) {
      toast.error('Please select date and time');
      return;
    }
    if (!bookingForm.patientName || !bookingForm.patientPhone || !bookingForm.city || !bookingForm.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    if (bookingForm.bookingType === 'home_collection' && !bookingForm.address) {
      toast.error('Please enter address for home collection');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/diagnostics/book`, {
        provider_id: selectedProvider.id,
        test_ids: cart.map(t => t.id),
        booking_type: bookingForm.bookingType,
        booking_date: bookingForm.bookingDate,
        booking_time: bookingForm.bookingTime,
        patient_name: bookingForm.patientName,
        patient_phone: bookingForm.patientPhone,
        patient_email: bookingForm.patientEmail,
        address: bookingForm.address,
        city: bookingForm.city,
        pincode: bookingForm.pincode,
        payment_method: bookingForm.paymentMethod
      });

      toast.success('Booking confirmed successfully!');
      setShowBookingDialog(false);
      setCart([]);
      loadData();
      
      // Show booking confirmation
      alert(`Booking Confirmed!\n\nTracking ID: ${response.data.booking.tracking_id}\nDate: ${response.data.booking.date}\nTime: ${response.data.booking.time}\nTotal: ₹${response.data.booking.total_amount}`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await axios.put(`${API}/diagnostics/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'sample_collected': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-6 pt-24 flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <FlaskConical className="w-8 h-8 text-emerald-600" />
              Book Diagnostics
            </h1>
            <p className="text-gray-600 mt-1">Book lab tests from top diagnostic centers</p>
          </div>
          
          {/* Cart Button */}
          <Button 
            onClick={() => setShowCartDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700 relative"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Cart ({cart.length})
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </Button>
        </div>

        <Tabs defaultValue="tests" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="tests">Tests</TabsTrigger>
            <TabsTrigger value="providers">Labs</TabsTrigger>
            <TabsTrigger value="bookings">My Bookings</TabsTrigger>
          </TabsList>

          {/* Tests Tab */}
          <TabsContent value="tests" className="space-y-6">
            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search tests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'blood_test', 'full_body', 'covid'].map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className={selectedCategory === cat ? 'bg-emerald-600' : ''}
                  >
                    {cat === 'all' ? 'All' : cat === 'blood_test' ? 'Blood Tests' : cat === 'full_body' ? 'Health Packages' : 'COVID'}
                  </Button>
                ))}
              </div>
            </div>

            {/* Tests Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTests.map(test => (
                <Card key={test.id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">{test.name}</h3>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {test.category === 'blood_test' ? '🩸 Blood Test' : 
                           test.category === 'full_body' ? '🏥 Health Package' : '🦠 COVID'}
                        </Badge>
                      </div>
                      {test.home_collection_available && (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          <Home className="w-3 h-3 mr-1" />
                          Home
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{test.description}</p>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{test.report_time}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        {test.discounted_price ? (
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-emerald-600">₹{test.discounted_price}</span>
                            <span className="text-sm text-gray-400 line-through">₹{test.price}</span>
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              {Math.round((1 - test.discounted_price / test.price) * 100)}% OFF
                            </Badge>
                          </div>
                        ) : (
                          <span className="text-lg font-bold text-emerald-600">₹{test.price}</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => addToCart(test)}
                        disabled={cart.find(t => t.id === test.id)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {cart.find(t => t.id === test.id) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map(provider => (
                <Card key={provider.id} className="hover:shadow-xl transition-all overflow-hidden">
                  <div className="h-2 bg-gradient-to-r from-emerald-500 to-teal-500" />
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center p-2">
                        <img 
                          src={provider.logo} 
                          alt={provider.name}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=' + provider.name[0]; }}
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-800">{provider.name}</h3>
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium">{provider.rating}</span>
                          <span className="text-gray-500">({provider.reviews_count.toLocaleString()} reviews)</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{provider.description}</p>
                    
                    <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      <span className="line-clamp-1">{Array.isArray(provider.locations) ? provider.locations.join(', ') : provider.locations}</span>
                    </div>
                    
                    <div className="flex gap-2 mb-4">
                      {provider.home_collection && (
                        <Badge className="bg-green-100 text-green-800">
                          <Home className="w-3 h-3 mr-1" />
                          Home Collection
                        </Badge>
                      )}
                      {provider.walk_in && (
                        <Badge className="bg-blue-100 text-blue-800">
                          <Building2 className="w-3 h-3 mr-1" />
                          Walk-in
                        </Badge>
                      )}
                    </div>
                    
                    <Button 
                      onClick={() => handleStartBooking(provider)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={cart.length === 0}
                    >
                      {cart.length === 0 ? 'Add tests to cart first' : `Book with ${provider.name}`}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {bookings.length === 0 ? (
              <Card className="p-12 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Bookings Yet</h3>
                <p className="text-gray-500">Your diagnostic test bookings will appear here</p>
              </Card>
            ) : (
              bookings.map(booking => (
                <Card key={booking.id} className="overflow-hidden">
                  <div className={`h-1 ${booking.booking_status === 'confirmed' ? 'bg-blue-500' : 
                                         booking.booking_status === 'completed' ? 'bg-green-500' : 
                                         booking.booking_status === 'cancelled' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg">{booking.provider_name}</h3>
                          <Badge className={getStatusColor(booking.booking_status)}>
                            {booking.booking_status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><span className="font-medium">Tracking ID:</span> {booking.tracking_id}</p>
                          <p><span className="font-medium">Tests:</span> {booking.test_names?.join(', ')}</p>
                          <p><span className="font-medium">Date:</span> {booking.booking_date} | {booking.booking_time}</p>
                          <p><span className="font-medium">Type:</span> {booking.booking_type === 'home_collection' ? '🏠 Home Collection' : '🏥 Walk-in'}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600">₹{booking.total_amount}</p>
                        <Badge className={booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {booking.payment_status === 'paid' ? '✓ Paid' : '⏳ Pay at center'}
                        </Badge>
                        
                        {booking.booking_status === 'confirmed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => cancelBooking(booking.id)}
                          >
                            Cancel Booking
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cart Dialog */}
      <Dialog open={showCartDialog} onOpenChange={setShowCartDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Your Cart ({cart.length} tests)
            </DialogTitle>
          </DialogHeader>
          
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(test => (
                <div key={test.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{test.name}</p>
                    <p className="text-sm text-emerald-600 font-semibold">₹{test.discounted_price || test.price}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFromCart(test.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">₹{cart.reduce((sum, t) => sum + (t.discounted_price || t.price), 0)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                  <span>Home collection (if applicable)</span>
                  <span>+₹100</span>
                </div>
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span className="text-emerald-600">₹{getCartTotal()}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCartDialog(false)}>
              Continue Shopping
            </Button>
            {cart.length > 0 && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  setShowCartDialog(false);
                  // Scroll to providers section
                }}
              >
                Select Lab <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {bookingStep === 1 && 'Select Date & Time'}
              {bookingStep === 2 && 'Patient Details'}
              {bookingStep === 3 && 'Payment & Confirm'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Step 1: Date & Time */}
          {bookingStep === 1 && (
            <div className="space-y-4">
              {/* Booking Type */}
              <div>
                <Label className="text-sm font-medium">Collection Type</Label>
                <RadioGroup
                  value={bookingForm.bookingType}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, bookingType: value })}
                  className="flex gap-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="home_collection" id="home" />
                    <Label htmlFor="home" className="flex items-center cursor-pointer">
                      <Home className="w-4 h-4 mr-2 text-emerald-600" />
                      Home Collection (+₹100)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="walk_in" id="walkin" />
                    <Label htmlFor="walkin" className="flex items-center cursor-pointer">
                      <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                      Walk-in
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Date Selection */}
              <div>
                <Label htmlFor="date">Select Date *</Label>
                <Input
                  id="date"
                  type="date"
                  min={getMinDate()}
                  value={bookingForm.bookingDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="mt-1"
                />
              </div>

              {/* Time Slots */}
              {bookingForm.bookingDate && (
                <div>
                  <Label>Select Time Slot *</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {timeSlots.map(slot => (
                      <Button
                        key={slot.time}
                        variant={bookingForm.bookingTime === slot.time ? 'default' : 'outline'}
                        size="sm"
                        disabled={!slot.available}
                        onClick={() => setBookingForm({ ...bookingForm, bookingTime: slot.time })}
                        className={bookingForm.bookingTime === slot.time ? 'bg-emerald-600' : ''}
                      >
                        {slot.time}
                        {!slot.available && ' (Full)'}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Patient Details */}
          {bookingStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Patient Name *</Label>
                <Input
                  id="name"
                  value={bookingForm.patientName}
                  onChange={(e) => setBookingForm({ ...bookingForm, patientName: e.target.value })}
                  placeholder="Enter patient name"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={bookingForm.patientPhone}
                  onChange={(e) => setBookingForm({ ...bookingForm, patientPhone: e.target.value })}
                  placeholder="10-digit mobile number"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={bookingForm.patientEmail}
                  onChange={(e) => setBookingForm({ ...bookingForm, patientEmail: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>

              {bookingForm.bookingType === 'home_collection' && (
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Textarea
                    id="address"
                    value={bookingForm.address}
                    onChange={(e) => setBookingForm({ ...bookingForm, address: e.target.value })}
                    placeholder="Enter complete address for sample collection"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={bookingForm.city}
                    onChange={(e) => setBookingForm({ ...bookingForm, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    value={bookingForm.pincode}
                    onChange={(e) => setBookingForm({ ...bookingForm, pincode: e.target.value })}
                    placeholder="6-digit pincode"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Payment & Confirm */}
          {bookingStep === 3 && (
            <div className="space-y-4">
              {/* Booking Summary */}
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <h4 className="font-semibold mb-3">Booking Summary</h4>
                  <div className="text-sm space-y-2">
                    <p><span className="text-gray-500">Lab:</span> {selectedProvider?.name}</p>
                    <p><span className="text-gray-500">Date:</span> {bookingForm.bookingDate}</p>
                    <p><span className="text-gray-500">Time:</span> {bookingForm.bookingTime}</p>
                    <p><span className="text-gray-500">Type:</span> {bookingForm.bookingType === 'home_collection' ? 'Home Collection' : 'Walk-in'}</p>
                    <p><span className="text-gray-500">Tests:</span> {cart.map(t => t.name).join(', ')}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <div>
                <Label className="text-sm font-medium">Payment Method</Label>
                <RadioGroup
                  value={bookingForm.paymentMethod}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, paymentMethod: value })}
                  className="mt-2 space-y-2"
                >
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="pay_at_center" id="pay_center" />
                    <Label htmlFor="pay_center" className="flex items-center cursor-pointer flex-1">
                      <Banknote className="w-4 h-4 mr-2 text-emerald-600" />
                      Pay at Center / To Collection Agent
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg">
                    <RadioGroupItem value="online" id="pay_online" />
                    <Label htmlFor="pay_online" className="flex items-center cursor-pointer flex-1">
                      <CreditCard className="w-4 h-4 mr-2 text-blue-600" />
                      Pay Online Now
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center text-xl font-bold pt-4 border-t">
                <span>Total Amount</span>
                <span className="text-emerald-600">₹{getCartTotal()}</span>
              </div>

              <div className="flex items-start gap-2 text-sm text-gray-500 bg-yellow-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 mt-0.5 text-yellow-600" />
                <p>By confirming, you agree to our terms and conditions. Cancellation is free up to 12 hours before the appointment.</p>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {bookingStep > 1 && (
              <Button variant="outline" onClick={() => setBookingStep(bookingStep - 1)}>
                Back
              </Button>
            )}
            
            {bookingStep < 3 ? (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setBookingStep(bookingStep + 1)}
                disabled={bookingStep === 1 && (!bookingForm.bookingDate || !bookingForm.bookingTime)}
              >
                Continue
              </Button>
            ) : (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleSubmitBooking}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Booking...
                  </>
                ) : (
                  'Confirm Booking'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiagnosticsBooking;
